import { Clock, Video } from "lucide-react";
import React, { useEffect, useState } from "react";


interface CountdownProps {
  streamStartAt: string;
  onCountdownEnd: () => void;
}

const StreamCountdown: React.FC<CountdownProps> = ({
  streamStartAt,
  onCountdownEnd,
}) => {
  const [timeLeft, setTimeLeft] = useState({ h: 0, m: 0, s: 0 });
  const [isEnded, setIsEnded] = useState(false);

  useEffect(() => {
    const target = new Date(streamStartAt).getTime();

    const updateCountdown = () => {
      const now = Date.now();
      const diff = target - now;

      if (diff <= 0) {
        setTimeLeft({ h: 0, m: 0, s: 0 });
        if (!isEnded) {
          setIsEnded(true);
          onCountdownEnd();
        }
        return;
      }

      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor((diff / (1000 * 60)) % 60);
      const s = Math.floor((diff / 1000) % 60);

      setTimeLeft({ h, m, s });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [streamStartAt, onCountdownEnd, isEnded]);

  return (
    <div className="min-h-[calc(100vh-70px)] bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-xl w-full">

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center relative overflow-hidden">
          {/* Animated background glow */}
          {/* <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 animate-pulse"></div> */}

          {/* Icon */}
          <div className="mb-6 flex justify-center relative z-10">
            <div className="relative">
              <div className="absolute inset-0 bg-blue-500 rounded-full opacity-20 animate-ping"></div>
              <div className="relative bg-blue-100 p-4 rounded-full">
                <Clock className="w-12 h-12 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3 relative z-10">
            Streaming Will Start Soon
          </h1>

          {/* Description */}
          <p className="text-slate-600 mb-6 leading-relaxed relative z-10">
            The live streaming session is about to begin. Stay tuned — the
            stream will start automatically once the countdown ends.
          </p>

          {/* Countdown Display */}
          <div className="bg-slate-50 rounded-xl p-4 mb-6 space-y-3 relative z-10">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <Video className="w-4 h-4 text-slate-500" />
                <span className="text-slate-700">Stream Status</span>
              </div>
              <span className="text-blue-600 font-medium">Starting Soon</span>
            </div>

            {/* Countdown Timer */}
            <div className="flex justify-center gap-4 mt-4">
              <div className="flex flex-col items-center">
                <span className="text-3xl font-mono font-bold text-blue-700">
                  {timeLeft.h.toString().padStart(2, "0")}
                </span>
                <span className="text-xs text-slate-500 uppercase">Hours</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-3xl font-mono font-bold text-blue-700">
                  {timeLeft.m.toString().padStart(2, "0")}
                </span>
                <span className="text-xs text-slate-500 uppercase">
                  Minutes
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-3xl font-mono font-bold text-blue-700">
                  {timeLeft.s.toString().padStart(2, "0")}
                </span>
                <span className="text-xs text-slate-500 uppercase">
                  Seconds
                </span>
              </div>
            </div>
          </div>

          {/* Subtext */}
          <p className="text-xs text-slate-500 relative z-10">
            Please keep this page open — the live stream will begin
            automatically.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StreamCountdown;
